/* eslint-disable react/forbid-component-props */
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Popover as AriaPopover } from 'react-aria-components'
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components'

export type PopoverPlacement = AriaPopoverProps['placement'] | 'center'
const CENTER_PLACEMENT_EDGE_THRESHOLD = 20 // pixels from the edge of the viewport

export interface PopoverProps {
  /**
   * Defines a string value that labels the popover
   */
  'aria-label'?: string
  /**
   * Identifies the element (or elements) that labels the popover
   */
  'aria-labelledby'?: string
  /**
   * Identifies the element (or elements) that describes the popover
   */
  'aria-describedby'?: string
  /**
   * The content to display inside the popover
   */
  children: ReactNode
  /**
   * Whether the popover is currently open
   * @default false
   */
  isOpen?: boolean
  /**
   * The distance in pixels between the trigger element and the popover
   * @default 8
   */
  offset?: number
  /**
   * Callback fired when the popover open state changes
   */
  onOpenChange?: (isOpen: boolean) => void
  /**
   * The placement of the popover relative to the trigger element
   * @default 'bottom'
   */
  placement?: PopoverPlacement
  /**
   * Reference to the trigger element that opens the popover
   */
  triggerRef?: React.RefObject<HTMLElement>
}

function Popover({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  children,
  isOpen = false,
  offset = 8,
  onOpenChange,
  placement = 'bottom',
  triggerRef,
}: PopoverProps): JSX.Element {
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const [isNearEdge, setIsNearEdge] = useState(false)
  const isCenterPlacement = placement === 'center'

  useEffect(
    function useCenterPlacementOffset() {
      const popover = popoverRef.current
      if (!popover || !isCenterPlacement) return

      const updateCenterOffset = () => {
        const trigger = triggerRef?.current
        const popoverStyles = getComputedStyle(popover)
        const popoverHeight = popoverStyles
          .getPropertyValue('--trigger-anchor-point')
          .replace(/px/g, '')
          .split(' ')[1]

        if (trigger && popoverHeight) {
          const triggerStyles = getComputedStyle(trigger)
          const borderWidth = parseFloat(triggerStyles.borderWidth) || 0
          const totalHeight = trigger.clientHeight + borderWidth * 2

          popover.style.setProperty(
            '--center-offset',
            Number(popoverHeight) / 2 + totalHeight / 2 + offset + 'px',
          )
        }
      }

      if (isOpen) {
        requestAnimationFrame(updateCenterOffset)
      }
    },
    [isCenterPlacement, isOpen, offset, triggerRef],
  )

  useEffect(
    function useEdgeProximityDetection() {
      const trigger = triggerRef?.current
      if (!trigger || !isCenterPlacement || !isOpen) {
        return
      }

      const checkEdgeProximity = () => {
        const rect = trigger.getBoundingClientRect()
        const threshold = CENTER_PLACEMENT_EDGE_THRESHOLD

        const nearTop = rect.top < threshold
        const nearBottom = window.innerHeight - rect.bottom < threshold
        const nearLeft = rect.left < threshold
        const nearRight = window.innerWidth - rect.right < threshold

        const isNear = nearTop || nearBottom || nearLeft || nearRight
        setIsNearEdge(isNear)
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(checkEdgeProximity)
      })

      let resizeTimeout: ReturnType<typeof setTimeout>
      const debouncedCheckEdgeProximity = () => {
        clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(checkEdgeProximity, 100)
      }

      window.addEventListener('resize', debouncedCheckEdgeProximity)

      return () => {
        window.removeEventListener('resize', debouncedCheckEdgeProximity)
        clearTimeout(resizeTimeout)
        // reset after animation completes
        setTimeout(() => {
          setIsNearEdge(false)
        }, 300)
      }
    },
    [isCenterPlacement, isOpen, triggerRef],
  )

  return (
    <AriaPopover
      aria-describedby={ariaDescribedby}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={clsx(
        'react-aria-Popover focus-visible:ring',
        isCenterPlacement && !isNearEdge && 'center-placement',
      )}
      isOpen={isOpen}
      offset={offset}
      onOpenChange={onOpenChange}
      placement={isCenterPlacement ? 'top' : placement}
      ref={popoverRef}
      triggerRef={triggerRef}
    >
      {children}
    </AriaPopover>
  )
}

export default Popover
